// -- [Hero section with role/topic selectors]

import { useState, useRef, useEffect } from 'react'
import * as Select from '@radix-ui/react-select'
import * as Collapsible from '@radix-ui/react-collapsible'
import type { ContentConfig, RoleKey } from '@/types/content'
import type { CurrentCopy } from '@/lib/content'
import {
  HERO_SHARE_IMAGE_HEIGHT,
  HERO_SHARE_IMAGE_PATH,
  HERO_SHARE_IMAGE_WIDTH,
} from '@/lib/siteMeta'
import { POSITIONING_WHY } from '@/data/positioningWhyOptions'
import { MarkdownLike } from '@/components/MarkdownLike'
import { Button, SuccessTickIcon } from '@/components/ui/Button'
import {
  selectContentClass,
  selectItemClass,
  selectTriggerClass,
} from '@/components/ui/SelectTrigger'
import { DrivibleAnimatedLogo } from './DrivibleAnimatedLogo'
import { DrivibleWordmarkHeroSvg } from './DrivibleWordmarkHeroSvg'

function BurgerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  )
}

const HERO_NAV_LINKS = [
  { label: 'Features', href: '#features', umami: 'Nav - Features' },
  { label: 'Customers', href: '#customers', umami: 'Nav - Customers' },
  { label: 'Q&A', href: '#qa', umami: 'Nav - Q&A' },
  { label: 'Pricing', href: '#pricing', umami: 'Nav - Pricing' },
] as const

interface HeroProps {
  config: ContentConfig
  copy: CurrentCopy
  roleKey: RoleKey
  topicKey: string
  onRoleChange: (role: RoleKey) => void
  onTopicChange: (topic: string) => void
  onOpenSignup?: (source: string) => void
  /** -- When true, Setup button shows green success styling + tick (already submitted) */
  hasSubmittedDemo?: boolean
}

const ROLE_ORDER: RoleKey[] = ['service', 'sales', 'finance', 'management']

function shouldShowPaperToScannerHeroImage(roleKey: RoleKey, topicKey: string) {
  return (
    (roleKey === 'service' && topicKey === 'reduce-paperwork') ||
    (roleKey === 'management' && topicKey === 'save-80k')
  )
}

export function Hero({
  config,
  copy,
  roleKey,
  topicKey,
  onRoleChange,
  onTopicChange,
  onOpenSignup,
  hasSubmittedDemo = false,
}: HeroProps) {
  const role = config.roles[roleKey]
  const topics = role?.topics ?? []
  const [menuOpen, setMenuOpen] = useState(false)
  const [whyOpen, setWhyOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // -- [Close mobile menu when clicking outside]
  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  return (
    <section
      id="hero"
      className={`relative ${config.brand.gradientClass} px-6 py-8 md:px-12 md:py-12 overflow-x-hidden`}
    >
      <div className="max-w-6xl mx-auto min-w-0">
        {/* -- [Site header landmark for assistive tech and SEO structure audits] */}
        <header>
          {/* -- [Nav: inline links from lg (1024px); logo + burger below that] */}
          <nav
            className="flex items-center justify-between gap-4 mb-10 min-w-0"
            aria-label="Primary"
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 shrink">
              <DrivibleAnimatedLogo className="shrink-0" placement="hero" />
            </div>
            <div className="hidden lg:flex items-center gap-4 text-white text-sm font-medium shrink-0">
              {HERO_NAV_LINKS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="inline-flex items-center min-h-12 px-3 py-2 text-white/95 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  data-umami-event={item.umami}
                >
                  {item.label}
                </a>
              ))}
              {onOpenSignup && (
                <Button
                  variant={hasSubmittedDemo ? 'success' : 'onBrand'}
                  onClick={() => onOpenSignup('Setup Now')}
                  data-umami-event="Header - Setup Now"
                >
                  {hasSubmittedDemo && <SuccessTickIcon className="h-4 w-4 shrink-0" />}
                  Setup Now
                </Button>
              )}
            </div>
            <div className="lg:hidden relative shrink-0" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="inline-flex items-center justify-center min-h-12 min-w-12 p-3 rounded-md text-white hover:bg-white/10 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <BurgerIcon />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 py-2 w-[min(280px,90vw)] rounded-lg bg-white border border-line shadow-lg z-50 grid grid-cols-1 min-[400px]:grid-cols-2 gap-1"
                  role="menu"
                >
                  {HERO_NAV_LINKS.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-orange/10 hover:text-brand-orange cursor-pointer col-span-1"
                      data-umami-event={item.umami}
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  ))}
                  {onOpenSignup && (
                    <button
                      type="button"
                      role="menuitem"
                      className={`col-span-full w-full text-left px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 cursor-pointer ${
                        hasSubmittedDemo
                          ? 'text-green-700 bg-green-50 hover:bg-green-100'
                          : 'text-gray-900 hover:bg-brand-orange/10 hover:text-brand-orange'
                      }`}
                      onClick={() => {
                        onOpenSignup('Setup Now')
                        setMenuOpen(false)
                      }}
                      data-umami-event="Header - Setup Now"
                    >
                      {hasSubmittedDemo && (
                        <SuccessTickIcon className="h-4 w-4 shrink-0 text-green-600" />
                      )}
                      Setup Now
                    </button>
                  )}
                </div>
              )}
            </div>
          </nav>
        </header>

        {/* -- [Positioning H1: white on brand for AA contrast; yellow as underline accent only] */}
        <h1 className="text-3xl md:text-4xl font-bold leading-snug mb-5 max-w-4xl text-white motion-enter">
          <span className="block font-semibold">
            Want the{' '}
            <span className="relative inline-block italic font-bold">
              Best
              <span
                className="absolute left-0 right-0 -bottom-0.5 h-1 rounded-full bg-brand-yellow/90"
                aria-hidden
              />
            </span>{' '}
            Document Storage
          </span>
          <span className="block font-semibold mt-1">
            and Data Management Solution for
          </span>
          <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-2 font-semibold mt-1">
            <span className="relative inline-block italic font-bold">
              Aussie Car Dealerships?
              <span
                className="absolute left-0 right-0 -bottom-0.5 h-1 rounded-full bg-brand-yellow/90"
                aria-hidden
              />
            </span>
            <span>Then you want</span>
            <span
              className="inline-block h-[1.45em] w-[4.7em] shrink-0 text-white leading-none"
              role="img"
              aria-label="Drivible"
            >
              <DrivibleWordmarkHeroSvg
                className="block h-full w-full"
                dotFill="#ffbc48"
                dotStroke="#111827"
                dotStrokeWidth={4}
              />
            </span>
          </span>
        </h1>

        {/* -- [Teaser WHY + collapsible details] */}
        <div id="why-drivible" className="mb-8 positioning-why max-w-3xl">
          {POSITIONING_WHY.teaser.map((para, i) => (
            <p
              key={i}
              className={`font-medium text-white text-lg leading-relaxed positioning-paragraphs ${
                i > 0 ? 'mt-3' : ''
              }`}
            >
              <MarkdownLike text={para} asBlock={false} />
            </p>
          ))}
          <Collapsible.Root open={whyOpen} onOpenChange={setWhyOpen}>
            <Collapsible.Trigger
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white underline decoration-white/50 underline-offset-4 hover:decoration-white cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded"
              data-umami-event="Hero - More reasons"
            >
              {whyOpen ? 'Show less' : 'More reasons'}
              <span
                className={`inline-block transition-transform ${whyOpen ? 'rotate-180' : ''}`}
                aria-hidden
              >
                ▾
              </span>
            </Collapsible.Trigger>
            <Collapsible.Content className="mt-3 data-[state=open]:animate-none">
              {POSITIONING_WHY.details.map((para, i) => (
                <p
                  key={i}
                  className={`font-medium text-white/95 text-lg leading-relaxed positioning-paragraphs ${
                    i > 0 ? 'mt-3' : ''
                  }`}
                >
                  <MarkdownLike text={para} asBlock={false} />
                </p>
              ))}
            </Collapsible.Content>
          </Collapsible.Root>
          <p className="italic font-medium text-white text-lg mt-4 leading-relaxed positioning-closing">
            <MarkdownLike text={POSITIONING_WHY.closing} asBlock={false} />
          </p>
        </div>

        <p className="mb-2 text-white font-semibold text-lg">About you</p>
        {/* -- [Selectors row: "I am [role] and interested in [topic]"] */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <span className="text-white font-medium text-lg">I am</span>
          <Select.Root value={roleKey} onValueChange={(v) => onRoleChange(v as RoleKey)}>
            <Select.Trigger
              className={selectTriggerClass('onBrand', 'min-w-[200px]')}
              aria-label="Select role"
            >
              <Select.Value placeholder="Select role" />
              <Select.Icon className="text-gray-500" />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className={selectContentClass} position="popper">
                <Select.Viewport>
                  {ROLE_ORDER.map((r) => (
                    <Select.Item key={r} value={r} className={selectItemClass}>
                      <Select.ItemText>{config.roles[r]?.label ?? r}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
          <span className="text-white font-medium text-lg">and interested in</span>
          <Select.Root value={topicKey} onValueChange={onTopicChange}>
            <Select.Trigger
              className={selectTriggerClass('onBrand', 'min-w-[240px]')}
              aria-label="Select topic"
            >
              <Select.Value placeholder="Select topic" />
              <Select.Icon className="text-gray-500" />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className={selectContentClass} position="popper">
                <Select.Viewport>
                  {topics.map((t) => (
                    <Select.Item key={t.key} value={t.key} className={selectItemClass}>
                      <Select.ItemText>{t.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        {/* -- [Role/topic hero content — h2 because positioning statement is the page h1] */}
        <div className="max-w-3xl">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            {copy.heroHeadline}
          </h2>
          <p className="text-xl text-white mb-6">{copy.heroSubheadline}</p>
          {copy.heroBullets.length > 0 && (
            <ul className="space-y-2">
              {copy.heroBullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-2 text-white">
                  <span
                    className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-bold text-white"
                    aria-hidden
                  >
                    ✓
                  </span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
          {shouldShowPaperToScannerHeroImage(roleKey, topicKey) && (
            <div className="mt-8 max-w-4xl">
              <img
                src={HERO_SHARE_IMAGE_PATH}
                alt="Paper documents scanned into Drivible, stored and searchable in the cloud"
                title="Paper documents which are scanned into Drivible are then stored and searchable in the cloud"
                className="w-full h-auto rounded-xl border border-white shadow-lg bg-white/80"
                width={HERO_SHARE_IMAGE_WIDTH}
                height={HERO_SHARE_IMAGE_HEIGHT}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
